import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface AccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EMPTY_PASSWORDS = { current: "", next: "", confirm: "" };

const AccountDialog = ({ open, onOpenChange }: AccountDialogProps) => {
  const { user, fullName, updateFullName } = useAuth();
  const { toast } = useToast();
  const [name, setName] = useState(fullName);
  const [savingName, setSavingName] = useState(false);
  const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
  const [savingPassword, setSavingPassword] = useState(false);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (open) {
      setName(fullName);
      setPasswords(EMPTY_PASSWORDS);
    }
  }, [open, fullName]);

  const handleSaveName = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: "Name required", description: "Please enter your full name.", variant: "destructive" });
      return;
    }
    setSavingName(true);
    try {
      await updateFullName(trimmed);
      toast({ title: "Name updated", description: "Your name has been saved." });
    } catch (err: any) {
      toast({ title: "Could not update name", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      setSavingName(false);
    }
  };

  const handleSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.email) return;
    if (passwords.next.length < 6) {
      toast({ title: "Password too short", description: "Use at least 6 characters.", variant: "destructive" });
      return;
    }
    if (passwords.next !== passwords.confirm) {
      toast({ title: "Passwords don't match", description: "New password and confirmation must match.", variant: "destructive" });
      return;
    }
    setSavingPassword(true);
    try {
      // Re-authenticate so a left-open session can't be used to change the password.
      const { error: authError } = await supabase.auth.signInWithPassword({ email: user.email, password: passwords.current });
      if (authError) throw new Error("Current password is incorrect.");
      const { error } = await supabase.auth.updateUser({ password: passwords.next });
      if (error) throw error;
      setPasswords(EMPTY_PASSWORDS);
      toast({ title: "Password updated", description: "Use your new password next time you sign in." });
    } catch (err: any) {
      toast({ title: "Could not update password", description: err?.message || "Please try again.", variant: "destructive" });
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>My Account</DialogTitle>
          <DialogDescription>{user?.email}</DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="profile">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="profile" className="gap-1.5"><User className="h-3.5 w-3.5" /> Profile</TabsTrigger>
            <TabsTrigger value="password" className="gap-1.5"><Lock className="h-3.5 w-3.5" /> Password</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <form onSubmit={handleSaveName} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="account-name">Full name</Label>
                <Input id="account-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" autoComplete="name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="account-email">Email</Label>
                <Input id="account-email" value={user?.email || ""} disabled />
              </div>
              <Button type="submit" className="w-full" disabled={savingName || name.trim() === fullName}>
                {savingName && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save name
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="password">
            <form onSubmit={handleSavePassword} className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label htmlFor="account-current">Current password</Label>
                <Input id="account-current" type="password" value={passwords.current} onChange={(e) => setPasswords({ ...passwords, current: e.target.value })} autoComplete="current-password" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="account-new">New password</Label>
                <Input id="account-new" type="password" value={passwords.next} onChange={(e) => setPasswords({ ...passwords, next: e.target.value })} autoComplete="new-password" required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="account-confirm">Confirm new password</Label>
                <Input id="account-confirm" type="password" value={passwords.confirm} onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })} autoComplete="new-password" required />
              </div>
              <Button type="submit" className="w-full" disabled={savingPassword}>
                {savingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update password
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AccountDialog;
